<template>
  <b-card v-if="job" class="price-breakdown">
    <div class="card-title d-flex">
      <div
        class="job-img"
        :style="{
          backgroundImage: 'url(' + toolImage + ')',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }"
      ></div>
      <div class="d-flex flex-column justify-content-center align-self-start">
        <h2 class="job-name">{{ job.name }}</h2>
        <div class="job-rate d-flex flex-row">
          <rate
            :value="reviewRating"
            :has-label="false"
            :disabled="true"
          ></rate>
          <p>({{ reviewTotal }} review{{ reviewTotal > 1 ? 's' : '' }})</p>
        </div>
      </div>
    </div>
    <div class="price-breakdown__content">
      <b-row>
        <b-col md="4">Dates</b-col>
        <b-col class="d-flex flex-row justify-content-end align-items-center"
          >{{ startDate | parseDate('DD/M/YYYY') }}
          <b-img
            width="24"
            class="pay-arrow"
            :src="require('@/assets/icons/arrow-right-tail.svg')"
          ></b-img>
          {{ endDate | parseDate('DD/M/YYYY') }}</b-col
        >
      </b-row>
      <b-row>
        <b-col
          >${{ breakdown.tool_price | price }} x
          {{ breakdown.number_of_days }} day(s)</b-col
        >
        <b-col class="text-right"
          >${{ breakdown.total.sub_total | price }}</b-col
        >
      </b-row>
      <b-row>
        <b-col>GST ({{ breakdown.tax }}%)</b-col>
        <b-col class="text-right">${{ breakdown.total.tax | price }}</b-col>
      </b-row>
      <b-row class="pay-total">
        <b-col><h3>Total</h3></b-col>
        <b-col class="text-right"
          ><h3>${{ breakdown.total.grand_total | price }}</h3></b-col
        >
      </b-row>
    </div>
  </b-card>
</template>
<script>
/**
 * ==================================================================================
 * Price Breakdown
 * ==================================================================================
 **/

import { mapActions } from 'vuex'
import dayjs from 'dayjs'
import Form from '@/utils/form'
import Job from '@/models/Job'
import Rate from '@/components/inputs/Rate'

export default {
  components: {
    Rate
  },
  props: {
    job: {
      type: [Job, Object]
    },
    fee: {
      type: Number,
      default: 17
    },
    startDate: {
      type: Date,
      required: true
    },
    endDate: {
      type: Date,
      required: true
    }
  },
  watch: {
    job(value) {
      if (value) {
        this.refresh()
      }
    }
  },
  computed: {
    noOfDays() {
      if (!this.startDate || !this.endDate) return
      if (this.endDate.toString() === this.startDate.toString()) return 1
      return dayjs(this.endDate).diff(dayjs(this.startDate), 'day')
    },

    toolImage() {
      if (!this.job || !this.job.gallery || !this.job.gallery.length)
        return require('@/assets/images/img-placeholder.svg')
      return this.job.gallery[0].thumb_url || this.job.gallery[0].url
    },

    toolPrice() {
      if (!this.noOfDays || !this.job) return 'NA'
      return this.job.price * this.noOfDays
    },

    total() {
      if (!this.noOfDays || !this.job) return 'NA'
      return this.toolPrice + this.fee
    },

    review() {
      if (!this.job) return
      return this.job.reviews
    },

    reviewTotal() {
      return this.review ? this.review.total : 0
    },

    reviewRating() {
      return this.review ? this.review.average : 0
    }
  },
  data: function() {
    return {
      form: new Form({}),
      breakdown: {
        tool_price: 0,
        number_of_days: 0,
        service_fee: 0,
        tax: 0,
        total: {
          sub_total: 0,
          tax: 0,
          service_fee: 0,
          grand_total: 0
        }
      },
      imgProps: {
        center: true,
        blank: true,
        height: '90px',
        width: 'auto'
      }
    }
  },
  methods: {
    ...mapActions({
      rentCalculate: 'job/rentCalculate',
      addAlert: 'alert/addAlert'
    }),

    async refresh(message = null) {
      if (!this.job || this.form.$busy) return
      this.form.$setLoading(true)

      await this.rentCalculate({
        tool_id: this.job.id,
        from: this.parseDate(this.startDate, 'YYYY-MM-DD'),
        to: this.parseDate(this.endDate, 'YYYY-MM-DD')
      })
        .then(data => {
          this.form.$setLoading(false)
          this.breakdown = data.data.data
          this.$emit('new-price', {
            tool_price: this.breakdown.tool_price,
            grand_total: this.breakdown.total.grand_total
          })
          if (message) {
            this.addAlert({
              state: false,
              label: message
            })
          }
        })
        .catch(error => {
          this.form.$setLoading(false)
          this.form.$handleError(error)

          this.addAlert({
            state: false,
            label:
              (this.form.$getError('to') && this.form.$getError('to')[0]) ||
              (this.form.$getError('from') && this.form.$getError('from')[0]) ||
              this.form.$errorMessage
          })
        })
    }
  },
  async created() {
    await this.refresh()
  }
}
</script>
<style lang="scss" scoped>
.price-breakdown {
  .card-title {
    padding-bottom: 25px;
    margin-bottom: 45px;
    border-bottom: 1px solid #e3e3e3;
  }

  .card-body {
    padding: 1.8rem 2.65rem;
  }

  .job-name {
    font-size: 1.7rem;
    line-height: 1.7rem;
  }

  .job-img {
    width: 92.5px;
    height: 90px;
    margin-right: 20px;
    border-radius: 5px;
    border: 1px solid #efefef;
  }

  .job-rate {
    > p {
      font-size: 0.9rem;
      line-height: 1.1rem;
      color: #6c6c6c;
    }

    .rate {
      margin-right: 5px;
    }
  }

  &__content {
    font-size: 0.9rem;

    .row {
      margin-bottom: 25px;
    }

    .pay-arrow {
      margin: 0 10px 0 25px;
    }

    .pay-info {
      margin-left: 17.5px;
      cursor: pointer;
    }

    .pay-total {
      font-weight: 900;
      margin-top: 40px;
      margin-bottom: 10px;
      color: #444a51;
    }
  }
}
</style>
